import { formatTime } from "@clipboard-health/date-time";
import { Illustration, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Skeleton, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { useOpenGeoLocationContext } from "@src/appV2/redesign/OpenGeoLocation/useOpenGeoLocation";
import { formatTravelTimeForDisplay } from "@src/appV2/redesign/utils/formatTravelTimeForDisplay";
import { differenceInHours, parseISO } from "date-fns";

import { ShiftDetailsInnerCard } from "../InnerCard";
import type { ArrivalContext, ClockInContext, ParkingContext } from "./types";

export interface ParkingCardContentProps {
  arrivalContext: ArrivalContext;
  clockInContext: ClockInContext;
  parkingContext: ParkingContext;
  estimatedTravelTime?: number;
  isEstimatedTravelTimeLoading: boolean;
  shiftStart: string;
  isNfcShift: boolean;
  workplaceTimezone: string;
  currentDate: Date;
}

export function ParkingCardContent(props: ParkingCardContentProps) {
  const {
    arrivalContext,
    clockInContext,
    parkingContext,
    estimatedTravelTime,
    isEstimatedTravelTimeLoading,
    shiftStart,
    isNfcShift,
    workplaceTimezone,
    currentDate,
  } = props;

  const { href, onClick: onGetDirections } = useOpenGeoLocationContext();

  const enforceNfcTimesheetBounds =
    useCbhFlag(CbhFeatureFlag.ENFORCE_NFC_TIMESHEET_BOUNDS, {
      defaultValue: false,
    }) && isNfcShift;

  const getArrivedMessage = () => {
    const now = currentDate;
    const shiftStartTime = parseISO(shiftStart);
    const hoursUntilShift = differenceInHours(shiftStartTime, now);

    if (hoursUntilShift > 24) {
      return "You've arrived!";
    }

    if (isDefined(clockInContext.earliestClockInTime) && isDefined(workplaceTimezone)) {
      const formattedClockInTime = formatTime(clockInContext.earliestClockInTime, {
        timeZone: workplaceTimezone,
      });
      return `You've arrived! You'll be able to clock in at ${formattedClockInTime}`;
    }

    if (enforceNfcTimesheetBounds) {
      return "You've arrived! Clock in to your shift to begin earning";
    }

    return "You've arrived! Clock in to your shift";
  };

  const getDisplayMessage = () => {
    switch (arrivalContext.state) {
      case "arrived": {
        return getArrivedMessage();
      }

      case "traveling": {
        return isDefined(estimatedTravelTime)
          ? `You are a ${formatTravelTimeForDisplay(Math.max(estimatedTravelTime, 1))} drive away`
          : "";
      }

      case "ready-to-travel": {
        return isDefined(estimatedTravelTime)
          ? `${formatTravelTimeForDisplay(estimatedTravelTime)} drive from home`
          : "";
      }

      case "not-ready-to-travel": {
        return "Your shift is scheduled";
      }

      case "unknown": {
        return "Location unavailable";
      }

      default: {
        return "";
      }
    }
  };

  const shouldShowGetDirections = arrivalContext.state !== "arrived";
  const isInTerminalState = ["arrived", "not-ready-to-travel", "unknown"].includes(
    arrivalContext.state
  );

  return (
    <ShiftDetailsInnerCard
      footer={
        shouldShowGetDirections && (
          <Button
            fullWidth
            variant="outlined"
            size="medium"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_GET_DIRECTIONS_TAPPED, {
                arrivalState: arrivalContext.state,
              });
              if (onGetDirections) {
                onGetDirections();
              } else {
                window.open(href, "_blank");
              }
            }}
          >
            Get directions
          </Button>
        )
      }
    >
      <CardContent>
        <Stack spacing={4} alignItems="center" sx={{ paddingX: 4, paddingY: 4 }}>
          <Illustration type="driving" />

          {!isInTerminalState && isEstimatedTravelTimeLoading ? (
            <Stack direction="column" justifyContent="center" alignItems="center">
              <Skeleton variant="text" width={150} height={25} data-testid="travel-time-loading" />
            </Stack>
          ) : (
            <Text semibold variant="h5" sx={{ textAlign: "center" }} data-testid="travel-time-text">
              {getDisplayMessage()}
            </Text>
          )}

          {parkingContext.isLoading && (
            <Skeleton
              variant="text"
              width={200}
              height={20}
              data-testid="parking-instructions-loading"
            />
          )}

          {isDefined(parkingContext.instructions) && (
            <Text
              variant="body2"
              sx={{ textAlign: "center" }}
              data-testid="parking-instructions-text"
            >
              Parking instructions: {parkingContext.instructions}
            </Text>
          )}
        </Stack>
      </CardContent>
    </ShiftDetailsInnerCard>
  );
}
